/*
 * <pdp-buy-dock>
 * The floating mobile buy capsule on the product page.
 *
 * It owns no buy logic of its own. Both buttons forward the tap to the real
 * controls inside <product-info>, so the variant id, the quantity and GoKwik's
 * own wiring stay in exactly one place — GoKwik binds to `#gokwik-buy-now` by
 * selector and that button carries an inline handler, so a second copy of it
 * would break checkout and ship an `on*` attribute we don't allow.
 *
 * Visibility is the inverse of the in-page buy row's: an IntersectionObserver
 * hides the capsule while that row is on screen and reveals it once it leaves.
 *
 * Writes two custom properties on <html>:
 *   --mobile-dock-height  the space the capsule occupies. Constant while the
 *                         dock is mounted, so the footer reserve never jumps.
 *                         Read by mobile-dock.css and the anzu-aplus-* scripts.
 *   --pdp-dock-lift       the same height, but live (0 while hidden). Drives the
 *                         chat launcher's clearance in pdp-buy-dock.css.
 */
(function () {
  if (customElements.get('pdp-buy-dock')) return;

  // The row has to be 8px clear of the fold before it counts as on screen,
  // otherwise the capsule flickers as the row grazes the viewport edge.
  var ROOT_MARGIN = '0px 0px -8px 0px';

  class PdpBuyDock extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.shell = this.querySelector('[data-dock-shell]');
      this.addBtn = this.querySelector('[data-dock-add]');
      this.buyBtn = this.querySelector('[data-dock-buy]');
      this.addLabel = this.querySelector('[data-dock-add-label]');

      this.row = this.findRow();
      if (!this.row || !this.shell) {
        // No buy row on this page (sold out, or a template without one).
        this.remove();
        return;
      }

      this.sourceAdd = this.row.querySelector('.product-form__submit');
      this.sourceBuy = document.getElementById('gokwik-buy-now');

      if (!this.sourceAdd && !this.sourceBuy) {
        this.remove();
        return;
      }

      this.bindButtons();
      this.syncFromSource();
      this.watchSource();
      this.observeRow();
      this.measure();

      this.onResize = (window.theme && theme.utils && theme.utils.debounce)
        ? theme.utils.debounce(this.measure.bind(this), 100)
        : this.measure.bind(this);
      window.addEventListener('resize', this.onResize);

      // Let the first state paint without the reveal transition running.
      var el = this;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.setAttribute('data-ready', '');
        });
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.sourceWatcher) this.sourceWatcher.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      document.documentElement.style.setProperty('--pdp-dock-lift', '0px');
      document.documentElement.style.setProperty('--mobile-dock-height', '0px');
    }

    /*
     * The main product's buy row, not a recommendation's or a bundle's.
     * `#gokwik-buy-now` is an id, so it is the sharpest anchor available;
     * <product-info> is the fallback when GoKwik's Buy Now is turned off.
     */
    findRow() {
      var buy = document.getElementById('gokwik-buy-now');
      var row = buy && buy.closest('.buy-buttons');
      if (row) return row;

      return document.querySelector('product-info .buy-buttons')
        || document.querySelector('.buy-buttons');
    }

    bindButtons() {
      if (this.addBtn) {
        if (!this.sourceAdd) {
          this.addBtn.remove();
          this.addBtn = null;
          this.shell.classList.add('pdp-dock__shell--single');
        } else {
          this.addBtn.addEventListener('click', this.proxyTo.bind(this, 'sourceAdd'));
        }
      }

      if (this.buyBtn) {
        if (!this.sourceBuy) {
          this.buyBtn.remove();
          this.buyBtn = null;
          this.shell.classList.add('pdp-dock__shell--single');
        } else {
          this.buyBtn.addEventListener('click', this.proxyTo.bind(this, 'sourceBuy'));
        }
      }
    }

    proxyTo(key, event) {
      event.preventDefault();
      var target = this[key];
      if (!target || target.disabled) return;
      target.click();
    }

    /*
     * Mirror the real Add to cart button rather than re-deriving its state:
     * that keeps "Pre-order" / "Sold out" wording and every translation correct
     * for free, and keeps the dock honest when a variant goes out of stock.
     */
    syncFromSource() {
      if (!this.sourceAdd || !this.addBtn) return;

      var isDisabled = this.sourceAdd.disabled;
      this.addBtn.disabled = isDisabled;
      if (this.buyBtn) this.buyBtn.disabled = isDisabled;

      if (this.addLabel) {
        var text = this.sourceAdd.querySelector('.btn-text');
        var label = (text ? text.textContent : this.sourceAdd.textContent).trim();
        if (label && label !== this.addLabel.textContent) this.addLabel.textContent = label;
      }
    }

    watchSource() {
      if (!this.sourceAdd || !window.MutationObserver) return;

      this.sourceWatcher = new MutationObserver(this.syncFromSource.bind(this));
      this.sourceWatcher.observe(this.sourceAdd, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    observeRow() {
      if (!window.IntersectionObserver) {
        this.setVisible(true);
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          this.setVisible(!entries[0].isIntersecting);
        }.bind(this),
        { threshold: 0, rootMargin: ROOT_MARGIN }
      );
      this.observer.observe(this.row);
    }

    setVisible(visible) {
      if (this.visible === visible) return;
      this.visible = visible;
      this.toggleAttribute('data-visible', visible);
      document.documentElement.style.setProperty(
        '--pdp-dock-lift',
        visible ? this.occupiedHeight() + 'px' : '0px'
      );
    }

    /* Capsule height plus the gutter and the safe-area inset below it. */
    occupiedHeight() {
      var offset = parseFloat(getComputedStyle(this).bottom);
      return this.offsetHeight + (isNaN(offset) ? 0 : offset);
    }

    measure() {
      // `offsetParent` is null for any fixed element, so it can't stand in for
      // "hidden" here — read display directly. On desktop the dock is display:none.
      var hidden = getComputedStyle(this).display === 'none';
      var height = hidden ? 0 : this.occupiedHeight();
      document.documentElement.style.setProperty('--mobile-dock-height', height + 'px');
      if (this.visible) {
        document.documentElement.style.setProperty('--pdp-dock-lift', height + 'px');
      }
    }
  }

  customElements.define('pdp-buy-dock', PdpBuyDock);

  window.theme = window.theme || {};
  window.theme.PdpBuyDock = PdpBuyDock;
})();
